import { v4 as uuid } from 'uuid';
import { Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, UploadTask } from 'firebase/storage';
import { useUserInfo } from '@xolv-mybrightlink/common';
import { useFirebaseUploadStorage } from '../../../../firebase/useFirebaseStorage';
import { useCurrentUserGuid } from '../../../../firebase/useCollection';
import { extname } from 'path';
import { useCallback } from 'react';
import { Message, MessageType } from '../../type';
import { useSendMessage } from './useSendMessage';
import { useCareTeam } from '../store/useCareTeam';
import { useUploadingMessage } from '../store/useUploadingMessages';
import { displayFullName } from '@xolv-mybrightlink/common';

type UploadTasks = {
  [channelGuid: string]:
    | {
        [messageGuid: string]:
          | {
              file: File;
              uploadTask: UploadTask;
              canceled?: boolean;
            }
          | undefined;
      }
    | undefined;
};

const uploadTasks: UploadTasks = {};

const storeUploadTask = (
  channelGuid: string,
  messageGuid: string,
  file: File,
  uploadTask: UploadTask,
) => {
  const byChannelGuid = uploadTasks[channelGuid] ?? {};
  byChannelGuid[messageGuid] = { file, uploadTask };
  uploadTasks[channelGuid] = byChannelGuid;
};

const removeUploadTask = (channelGuid: string, messageGuid: string) => {
  const byChannelGuid = uploadTasks[channelGuid];
  if (byChannelGuid) {
    delete byChannelGuid[messageGuid];
  }
};

const getMessageType = (file: File): MessageType | null => {
  const fileType = file.type;
  if (fileType.startsWith('image')) return 'IMAGE';
  if (fileType.startsWith('video')) return 'VIDEO';
  if (fileType === 'application/pdf') return 'PDF';
  if (isWordDocumentFile(file)) return 'WORD';

  return null;
};

const getMediaType = (file: File): string => {
  if (isWordDocumentFile(file)) {
    return getFileExtension(file.name);
  }
  return file.type.split('/')[1];
};

const getFileExtension = (filePath: string): string => {
  return extname(filePath).substring(1);
};

const isWordDocumentFile = (file: File): boolean => {
  const fileType = file.type;

  return (
    fileType === 'application/msword' ||
    fileType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  );
};

const translateMessageType = (messageType: MessageType): string => {
  switch (messageType) {
    case 'IMAGE':
      return 'an image';
    case 'VIDEO':
      return 'a video';
    case 'PDF':
      return 'a PDF file';
    case 'WORD':
      return 'a Word Document file';
    case 'TEXT':
      return '';
  }
};

const useSendFile = (
  channelGuid: string,
): ((message: Message, file: File) => void) => {
  const [, dispatch] = useCareTeam();
  const storage = useFirebaseUploadStorage();
  const sendMessage = useSendMessage(channelGuid);
  const userInfo = useUserInfo();

  return useCallback(
    (message, file) => {
      const {
        channelGuid,
        guid: messageGuid,
        messageType,
        authorGuid,
      } = message;
      const mediaType = getMediaType(file);
      const url = URL.createObjectURL(file);
      message.url = url;
      const storageRef = ref(
        storage,
        `/media/user/${authorGuid}/chat-channel/${channelGuid}/${messageGuid}.${mediaType}`,
      );
      const uploadTask = uploadBytesResumable(storageRef, file, {
        contentDisposition: `inline; filename="${file.name}"`,
        customMetadata: { name: file.name },
      });
      storeUploadTask(channelGuid, messageGuid, file, uploadTask);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          let progress: number | undefined;
          if (snapshot.state === 'running') {
            const { totalBytes, bytesTransferred } = snapshot;
            progress = totalBytes
              ? Math.round((bytesTransferred / totalBytes) * 100)
              : 100;
          }
          dispatch({
            type: 'FILE_UPLOAD',
            message,
            state: snapshot.state,
            progress,
          });
        },
        () => {
          const upload = uploadTasks[channelGuid]?.[messageGuid];
          const state = upload?.canceled ? 'canceled' : 'error';
          if (state === 'canceled') {
            removeUploadTask(channelGuid, messageGuid);
          }
          URL.revokeObjectURL(url);
          dispatch({
            type: 'FILE_UPLOAD',
            message,
            state,
          });
        },
        async () => {
          const mediaPath = uploadTask.snapshot.ref.fullPath;
          const contentMessage = translateMessageType(messageType);
          const fullName = userInfo
            ? displayFullName({
                firstName: userInfo.first_name,
                lastName: userInfo.last_name,
              })
            : '';
          const content = `${fullName} shared ${contentMessage}`;
          removeUploadTask(channelGuid, messageGuid);
          await sendMessage(messageGuid, content, {
            mediaPath,
            messageType,
            mediaType,
          });
          URL.revokeObjectURL(url);
          dispatch({
            type: 'FILE_UPLOAD',
            message,
            state: 'success',
          });
        },
      );
    },
    [dispatch, sendMessage, storage, userInfo],
  );
};

export const useSendFiles = (
  channelGuid: string,
): ((files: File[]) => void) => {
  const authorGuid = useCurrentUserGuid();
  const sendFile = useSendFile(channelGuid);

  return useCallback(
    (files) => {
      for (const file of files) {
        const messageType = getMessageType(file);
        if (!messageType) {
          return;
        }
        const message: Message = {
          guid: uuid(),
          channelGuid,
          content: '',
          authorGuid,
          messageType,
          createdDate: Timestamp.now(),
          localMessage: true,
          fileName: file.name,
        };
        sendFile(message, file);
      }
    },
    [channelGuid, sendFile, authorGuid],
  );
};

export const useResendFile = (
  channelGuid: string,
  messageGuid: string,
): (() => void) => {
  const sendFile = useSendFile(channelGuid);
  const message = useUploadingMessage(channelGuid, messageGuid);
  return () => {
    const file = uploadTasks[channelGuid]?.[messageGuid]?.file;
    if (message && file) {
      sendFile(message, file);
    }
  };
};

export const cancelUpload = (
  channelGuid: string,
  messageGuid: string,
): void => {
  const upload = uploadTasks[channelGuid]?.[messageGuid];
  if (upload) {
    upload.canceled = true;
    upload.uploadTask.cancel();
  }
};
